import { ChevronRight, Home as HomeIcon, Plus, HandCoinsIcon, Users, Timer } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Footer';
import MainHeader from '../MainHeader';
import ListingsSwiperView from '../ListingsSwiperView';
import { SharedSearchBar } from '../SearchBar';
import SearchOverlay from '../SearchOverlay';
import { useListings } from '../UseListings';

import heroImageSmall from '../../resources/hero-small.webp';
import heroImage from '../../resources/hero.webp';

import adelaide from '../../resources/landing-page/adelaide.webp';
import brisbane from '../../resources/landing-page/brisbane.webp';
import canberra from '../../resources/landing-page/canberra.webp';
import darwin from '../../resources/landing-page/darwin.webp';
import goldcoast from '../../resources/landing-page/goldcoast.webp';
import hobart from '../../resources/landing-page/hobart.webp';
import melbourne from '../../resources/landing-page/melbourne.webp';
import newcastle from '../../resources/landing-page/newcastle.webp';
import perth from '../../resources/landing-page/perth.webp';
import sydney from '../../resources/landing-page/sydney.webp';
import { useSignInModal } from '../../SignInModalContext';

// Types
interface PageContainerProps {
  children: ReactNode;
  className?: string;
}

interface FeatureCardProps {
  icon: any;
  title: string;
  description: string;
  gradient: string;
}

// Separate component definitions
export const PageContainer = ({ children, className = '' }: PageContainerProps) => (
  <div className={`mx-auto max-w-7xl ${className}`}>
    {children}
  </div>
);

export const SliderContainer = ({ children }: { children: ReactNode }) => (
  <div className="relative overflow-x-hidden">
    <div className="relative w-full left-1/2 -translate-x-1/2">
      {children}
    </div>
    <div className="absolute top-0 bottom-0 left-0 right-0 pointer-events-none z-20">
      <div className="mx-auto max-w-6xl px-4 relative h-full">
        <div className="hidden md:block absolute right-full top-0 bottom-0 w-8"
          style={{ background: 'linear-gradient(to right, white, transparent)' }}
        />
        <div className="hidden md:block absolute left-full top-0 bottom-0 w-8"
          style={{ background: 'linear-gradient(to left, white, transparent)' }}
        />
      </div>
    </div>
  </div>
);

const HeroBackground = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [backgroundUrl, setBackgroundUrl] = useState(heroImageSmall);

  useEffect(() => {
    const img = new Image();
    img.src = heroImage;
    setBackgroundUrl(heroImage);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Base image layer */}
      <div
        className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-500 opacity-75
                   ${isLoaded ? 'blur-none' : 'backdrop-blur-[2px]'}`}
        style={{
          backgroundImage: `url(${backgroundUrl})`
        }}
      />

      {/* Color tint overlay */}
      <div className="absolute inset-0 opacity-40 mix-blend-soft-light">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#24ccf2] via-transparent to-transparent animate-pulse" />
        <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-transparent to-[#2476f2] animate-pulse delay-1000" />
      </div>

      {/* Darkening gradient for text contrast */}
      <div className="absolute inset-0 bg-black/20" />
    </div>
  );
};


const FeatureCard = ({ icon: Icon, title, description, gradient }: FeatureCardProps) => (
  <div className="relative rounded-2xl bg-gradient-to-br from-[#24ccf2]/30 via-[#2476f2]/30 to-purple-500/30 p-[2px] shadow-lg">
    <div className="relative h-full rounded-2xl bg-white p-6">
      {/* Centered icon with gradient background */}
      <div className="mb-6 flex justify-center">
        <div className="relative">
          {/* Subtle gradient background effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#24ccf2] via-[#2476f2] to-purple-500 rounded-2xl opacity-10 blur-xl" />
          <div className={`relative h-16 w-16 rounded-2xl ${gradient} flex items-center justify-center`}>
            <Icon className="h-8 w-8 text-white" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        <div className="absolute inset-0 rounded-2xl opacity-[0.08] blur-2xl" />
        <h3 className="relative text-xl font-semibold mb-3 text-gray-800 text-center">
          {title}
        </h3>
        <p className="relative text-gray-600 leading-relaxed text-center">
          {description}
        </p>
      </div>
    </div>
  </div>
);

const WhyChooseUs = () => {
  const features = [
    {
      icon: HandCoinsIcon,
      title: "Completely Free",
      description: "We're the only completely free Australian sharehouse website. There's no hidden restrictions behind a paywall. We don't even have a payment option.",
      gradient: "bg-gradient-to-br from-[#24ccf2] to-[#2476f2]"
    },
    {
      icon: Users,
      title: "Tailored To You",
      description: "Find the perfect Australian flatmate or room share tailored to local needs. Whether you're a student, professional, or homeowner, our platform connects compatible housemates in cities and regions nationwide.",
      gradient: "bg-gradient-to-br from-[#2476f2] to-purple-500"
    },
    {
      icon: Timer,
      title: "Post Your Room In No Time",
      description: "List your spare room or sharehouse quickly with our straightforward process. Reach thousands of potential Australian flatmates actively searching for shared accommodation without paying a cent.",
      gradient: "bg-gradient-to-br from-purple-500 to-[#24ccf2]"
    }
  ];

  return (
    <div className="py-12 md:py-20 overflow-x-hidden">
      {/* Section header with gradient line */}
      <div className="text-center mb-8 md:mb-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
          Why choose us?
        </h2>
        <div className="w-24 h-1 mx-auto rounded-full bg-gradient-to-r from-[#24ccf2] to-[#2476f2]" />
      </div>

      {/* Features grid */}
      <div className="grid gap-6 md:grid-cols-3 md:gap-8 px-4 max-w-6xl mx-auto">
        {features.map((feature, index) => (
          <FeatureCard key={index} {...feature} />
        ))}
      </div>
    </div>
  );
};

const HomeListings = () => {
  const { listings, isLoading } = useListings();

  return (
    <div className="pt-8 pb-8 sm:pb-16 sm:pt-8">
      <div className="flex items-center justify-between px-4 pb-6">
        <p className="text-xl font-medium">Listings in Sydney</p>
        <Link to="/search-results?localities=" className="flex items-center text-sm text-gray-500 hover:text-gray-700">
          View all
          <ChevronRight className="w-5 h-5 ml-1" />
        </Link>
      </div>
      <ListingsSwiperView
        listings={listings}
        isLoading={isLoading}
      />
    </div>
  );
};

// bg-[#e6f0f2]
const CityDirectory = () => {
  return (
    <section className="py-12 bg-[#f2f2f2] rounded-2xl">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-3">Find Affordable Sharehouses Across Australia</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">Browse available rooms in top Australian cities and discover your perfect sharehouse match at the best prices.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-5 gap-4">
          {[
            { name: 'Sydney', state: 'NSW', count: 1243, image: sydney },
            { name: 'Melbourne', state: 'VIC', count: 987, image: melbourne },
            { name: 'Brisbane', state: 'QLD', count: 654, image: brisbane },
            { name: 'Perth', state: 'WA', count: 432, image: perth },
            { name: 'Gold Coast', state: 'QLD', count: 287, image: goldcoast },
            { name: 'Adelaide', state: 'SA', count: 321, image: adelaide },
            { name: 'Canberra', state: 'ACT', count: 198, image: canberra },
            { name: 'Newcastle', state: 'NSW', count: 176, image: newcastle },
            { name: 'Hobart', state: 'TAS', count: 98, image: hobart },
            { name: 'Darwin', state: 'NT', count: 86, image: darwin }
          ].map((city, index) => {
            // Show only the first 8 cities on medium and small screens
            console.log("index ", index);
            if ((index === 8 || index === 9) && window.innerWidth < 1024) {
              return null;
            }
            // Create varied colors with more variety while keeping some brand elements
            const gradients = [
              "from-[#24ccf2]/80 to-[#2476f2]/80 hover:from-[#24ccf2]/90 hover:to-[#2476f2]/90", // Brand blue to brand blue
              "from-[#24ccf2]/80 to-purple-500/80 hover:from-[#24ccf2]/90 hover:to-purple-500/90", // Brand blue to purple
              "from-teal-400/80 to-[#2476f2]/80 hover:from-teal-400/90 hover:to-[#2476f2]/90", // Teal to brand blue
              "from-[#24ccf2]/80 to-blue-600/80 hover:from-[#24ccf2]/90 hover:to-blue-600/90", // Brand blue to dark blue
              "from-cyan-400/80 to-indigo-500/80 hover:from-cyan-400/90 hover:to-indigo-500/90", // Cyan to indigo
              "from-sky-400/80 to-[#2476f2]/80 hover:from-sky-400/90 hover:to-[#2476f2]/90", // Sky to brand blue
              "from-[#24ccf2]/80 to-indigo-600/80 hover:from-[#24ccf2]/90 hover:to-indigo-600/90", // Brand blue to indigo
              "from-blue-400/80 to-blue-700/80 hover:from-blue-400/90 hover:to-blue-700/90", // Light blue to dark blue
              "from-cyan-400/80 to-blue-600/80 hover:from-cyan-400/90 hover:to-blue-600/90", // Cyan to blue
              "from-indigo-400/80 to-[#24ccf2]/80 hover:from-indigo-400/90 hover:to-[#24ccf2]/90" // Indigo to brand blue
            ];

            // Assign a gradient to each city
            const gradientClass = gradients[index % gradients.length];

            return (
              <a
                key={index}
                href={`/sharehouses-in-${city.name.toLowerCase()}`}
                className="group relative overflow-hidden rounded-lg shadow-sm h-20 lg:h-28 transition-all duration-300 hover:shadow-md hover:-translate-y-1"
                aria-label={`Find sharehouses in ${city.name}, ${city.state}`}
              >
                {/* Background Image with Gradient Overlay */}
                <div className="absolute inset-0">
                  <img
                    src={city.image}
                    alt={`Sharehouses in ${city.name}, ${city.state}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass} transition-opacity opacity-50 duration-300`}></div>
                </div>

                {/* Content */}
                <div className="absolute inset-0 p-2 lg:p-3 flex flex-col justify-center items-center text-white">
                  <h3 className="text-sm lg:text-lg font-bold text-center drop-shadow-md">{city.name}</h3>
                  <p className="text-white/90 text-xs drop-shadow-md">{city.state}</p>
                </div>
              </a>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/search-results?localities="
            className="bg-white border-2 border-[#00b8d9] text-[#00b8d9] hover:bg-[#00b8d9] hover:text-white transition-colors duration-300 font-medium py-2 px-6 rounded-full inline-flex items-center shadow-sm"
            aria-label="Explore sharehouses in all Australian cities"
          >
            Explore all locations
            <ChevronRight size={18} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
}

const LandingPage = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { openSignInModal } = useSignInModal();

  const handleSearchFocus = () => {
    const isMobile = window.innerWidth < 640;
    if (isMobile) {
      setIsSearchOpen(true);
    }
  };

  const handleItemsChange = (newItems) => {
    setSelectedItems(newItems);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <MainHeader />
      <PageContainer>
        <div className="relative min-h-[600px] flex items-center justify-center text-center py-20 ">
          <HeroBackground />
          <div className="relative z-10 max-w-4xl mx-auto px-4">
            {/* Text content with tight backgrounds */}
            <div className="flex flex-col items-center gap-4">
              {/* Main heading - background wraps tightly around content */}
              <div className="inline-flex flex-col items-center">
                <span className="relative">
                  <h1 className="relative text-5xl md:text-6xl font-bold text-white [filter:drop-shadow(-4px_4px_4px_rgb(0_0_0_/_0.2))_drop-shadow(4px_-4px_4px_rgb(0_0_0_/_0.2))]  px-4 py-2">
                    Find your next{' '}
                    <span className="inline-block animate-gradient bg-gradient-to-r text-[#26d7ff] bg-clip-text">
                      room
                    </span>
                  </h1>
                </span>
              </div>

              {/* Subheading - background adapts to text width */}
              <div className="inline-flex flex-col items-center mt-2">
                <span className="relative">
                  <p className="relative text-white text-xl [filter:drop-shadow(-4px_4px_4px_rgb(0_0_0_/_0.2))_drop-shadow(4px_-4px_4px_rgb(0_0_0_/_0.2))]  px-4 py-2">
                    Discover thousands of sharehouses or list your room{' '}
                    <span className="font-bold">completely</span> free, no strings attached!
                  </p>
                </span>
              </div>
            </div>

            {/* Search Box */}
            <div className="relative max-w-2xl mx-auto mt-12 mb-12 backdrop-blur-sm bg-white/90 rounded-xl p-1 shadow-xl">
              <SharedSearchBar
                selectedItems={selectedItems}
                onItemsChange={handleItemsChange}
                onFocus={handleSearchFocus}
              />
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => openSignInModal('/create-listing')}
              className="group px-8 py-4 bg-gradient-to-r from-[#24ccf2] to-[#2476f2] text-white rounded-xl 
                             hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300">
              <span className="flex items-center gap-2 text-lg font-medium">
                <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                List your room
              </span>
            </button>
          </div>
        </div>

        <HomeListings />
        <CityDirectory />
        <WhyChooseUs />
      </PageContainer>

      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        selectedItems={selectedItems}
        onItemsChange={handleItemsChange}
      />

      <Footer />
    </div>
  );
};

export default LandingPage;